import type { Experiences as ExperiencesContract } from '@luxuryescapes/contract-svc-experience'
import config from 'constants/config'
import { EmptyArray, last, sortBy } from 'lib/array/arrayUtils'
import findClosestAirport from 'lib/flights/findClosestAirport'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import type { ExperiencesPassport, ExperiencesPassportInclusion, ExperiencesPassportOfferSource } from 'components/Experiences/ExperiencesPassport/experiencesPassportTypes'
import { getExperienceCategoryIconSymbol } from 'lib/experiences/getExperienceCategoryIconSymbol'
import { formatDecimal } from 'lib/format/formatNumber'
import { formatLocationLine } from 'lib/format/formatAddress'
import { formatMinutesToHoursMinutes } from 'lib/datetime/time'
import { getSearchDistanceLabel } from 'components/OfferList/OfferListTiles/Support/searchDistanceLabel'

export function thingsToKnowSplitted(thingsToKnow: ExperiencesContract.ThingsToKnow): Array<App.ExperienceMarkdownSplitted> {
  return Object.entries(thingsToKnow)
    .filter(([, section]) => section.value && section.value.trim() !== '')
    .sort(([, a], [, b]) => a.sort - b.sort)
    .map(([key, section]) => ({
      key,
      title: section.label,
      content: section.value,
    }))
}

function redemptionLocationMap(location: ExperiencesContract.RedemptionLocation) {
  return {
    id: location.id,
    name: location.address,
    latitude: location.latitude,
    longitude: location.longitude,
    countryCode: location.countryCode,
    postalCode: location.postalCode,
    city: location.city,
    state: location.state,
  }
}

function getDiscountPercent(discount: number, grossPrice: number) {
  if (grossPrice > 0 && discount > 0) return Math.ceil((discount * 100) / grossPrice)
  return 0
}

export function experienceCategoryMap(serverCategory: any): App.ExperienceItemCategory {
  return {
    name: serverCategory.label,
    id: serverCategory.id,
    level: serverCategory.level === 0 ? 'parent' : 'child',
    parentId: serverCategory.parent ?? undefined,
    imageId: serverCategory.imageId ?? '4nz573bb82uubd28k0k',
  }
}

function getExperienceProductType(experience: ExperiencesContract.Offer): App.ExperienceProductType {
  if (experience.features.transfer) {
    return 'transfer'
  }

  return experience.offerType
}

interface ExperienceMapParams {
  experience: ExperiencesContract.Offer
  airports?: Array<App.Airport>
  currentRegionCode: string
}

const providerNameMap: { [key in ExperiencesContract.Provider]: App.ExperienceOffer['provider'] } = {
  LED: 'led',
  Rezdy: 'rez',
  Derbysoft: 'dby',
  Aspire: 'asp',
  Klook: 'klo',
  Collinson: 'cln',
  CustomLinc: 'cmc',
  Hubby: 'hby',
  Ingresso: 'ing',
  Transferz: 'tfz',
}

function getGiftableType(experience: ExperiencesContract.Offer): App.GiftableTypes | undefined {
  const offerGiftingAvailable = experience.giftingAvailable

  if (config.GIFT_EXPERIENCE_GIFT_CARDS) {
    return offerGiftingAvailable ? 'offer' : 'gift-card'
  }

  return undefined
}

export function experienceMap({
  experience,
  airports = [],
}: ExperienceMapParams): App.ExperienceOffer {
  const categories = experience.categories.map(experienceCategoryMap)
  const productType = getExperienceProductType(experience)
  const mapped: App.ExperienceOffer = {
    id: experience.id,
    parentType: 'experience',
    type: 'experience',
    analyticsType: 'experience',
    productType,
    name: experience.title,
    slug: experience.slug,
    bookingType: experience.bookingType,
    status: experience.status,
    provider: providerNameMap[experience.provider],
    providerId: experience.providerId,
    location: {
      // @ts-ignore: location field might be undefined
      name: experience.location?.description,
      // @ts-ignore: location field might be undefined
      latitude: experience.location?.latitude,
      // @ts-ignore: location field might be undefined
      longitude: experience.location?.longitude,
      // @ts-ignore: location field might be undefined
      timezone: experience.location?.timezone,
      city: experience.location?.marketingAddress?.find(address => address.lePlaceType === 'city')?.lePlaceName,
      marketingAddress: experience.location?.marketingAddress,
      countryCode: experience.redemptionLocations?.[0]?.countryCode,
    },
    airport: productType === 'transfer' ? findClosestAirport(airports, experience.location?.latitude, experience.location?.longitude) : undefined,
    // @ts-ignore: name field might be undefined
    redemptionLocations: experience.redemptionLocations?.map(redemptionLocationMap) ?? [],
    images: sortBy(experience.images ?? [], (image) => image.isHero ? 0 : 1, 'asc').map(image => {
      let url: string | undefined = image.urls[0].url
      let id = image.id
      // service often passes image URLs that are actually from our service and have the ID at the end
      // so lets extract that ID and use it instead so we can manipulate sizes
      if (!id && url?.includes(config.IMAGE_HOST)) {
        id = last(url.split('/')).split('.')[0]
      }
      if (id) {
        url = undefined
      }
      return {
        id,
        url,
        title: image.description,
      }
    }),
    video: experience.video,
    pricing: {
      price: experience.pricing.salesPrice,
      memberPrice: experience.pricing.memberDiscountPrice ?? 0,
      memberPriceCurrency: experience.pricing.memberDiscountPriceCurrency ?? 'AUD',
      memberDiscountPercentage: experience.pricing.memberDiscountPercentage ?? 0,
      value: experience.pricing.value,
      discountPercent: experience.pricing.discountPercentage,
      saleUnit: experience.pricing.ticketUnitLabel ?? 'total',
      fees: experience.pricing.taxesAndFees ? [{
        type: 'taxesAndFees',
        amount: experience.pricing.taxesAndFees,
      }] : [],
      mobileAppDiscountAmount: experience.pricing.appDiscountAmount,
      mobileAppDiscountPercentage: experience.pricing.appDiscountPercentage,
    },
    price: experience.baseSalesPrice.amount,
    discount: experience.discountPercentage,
    grossPrice: experience.grossPrice.amount,
    taxesAndFees: experience.taxesAndFees?.amount,
    pickupOptional: !experience.pickupMandatory,
    hasCalendar: experience.bookingType === 'CALENDAR-TIMESLOTS' || experience.bookingType === 'CALENDAR-NO-TIMESLOTS',
    hasTimeslots: experience.bookingType === 'CALENDAR-TIMESLOTS',
    primaryCategory: categories.find(cat => cat.level === 'parent') || categories[0],
    categories,
    cancellationPolicies: (experience.cancellationInfo.refundPolicies ?? []).map(policy => ({
      id: policy.id,
      periods: policy.periods,
      periodLabel: policy.periodLabel,
      groupId: policy.groupId ?? 1,
      value: policy.value,
    })),
    languages: experience.languages.map(lang => ({
      name: lang.name,
      id: lang.code,
    })),
    duration: {
      // @ts-ignore: duration field might be undefined
      from: experience.duration?.min,
      // @ts-ignore: duration field might be undefined
      to: experience.duration?.max,
    },
    voucher: {
      // @ts-ignore: Array.filter removes undefined values
      typesAccepted: [
        experience.voucherAccess?.byPrinted ? 'printed' : undefined,
        experience.voucherAccess?.byMixed ? 'mixed' : undefined,
        experience.voucherAccess?.byMobile ? 'digital' : undefined,
      ].filter(Boolean),
      required: !!experience.voucherAccess?.isRequired,
    },
    pickupPoints: (experience.pickupPoints ?? []).map(experiencePickupPointMap),
    leExclusive: config.BRAND === 'luxuryescapes' && !!experience.leExclusive,
    hasInstantConfirmation: !experience.needsProviderConfirmation,
    copy: {
      cancellationInfo: experience.cancellationInfo,
      availability: experience.operationalDays ?? undefined,
      // @ts-ignore: field might be null
      description: experience.shortDescription,
      highlights: experience.highlights?.map(highlight => highlight.label) ?? [],
      safetyInformation: experience.safetyInformation?.measures.filter(measure => measure.isActive)
        .map(measure => measure.copy?.description ?? measure.name) ?? [],
      meetingPoint: experience.meetingPoint,
      whereText: experience.where ?? undefined,
      included: experience.included ? experience.included : undefined,
      thingsToKnow: experience.thingsToKnowNew ? thingsToKnowSplitted(experience.thingsToKnowNew) : [],
      howToRedeem: experience.thingsToKnowNew?.howToRedeem?.value?.trim() || undefined,
      offerConditions: experience.thingsToKnowNew?.offerConditions?.value?.trim() || undefined,
      dealDescription: experience.dealDescription ? experience.dealDescription : undefined,
    },
    tagIds: experience.tagIds ?? [],
    noIndex: !experience.searchEngineMarketingEnabled,
    vendor: experience.vendor,
    ticketUnitLabel: experience.ticketUnitLabel ?? undefined,
    expirationDate: experience.expirationDate ? new Date(experience.expirationDate).toISOString() : undefined,
    bookByDate: experience.bookByDate ? new Date(experience.bookByDate).toISOString() : undefined,
    allowBuyNowBookLater: experience.allowBuyNowBookLater,
    allowGiftCard: experience.allowGiftCard,
    bookingCutoffHours: experience.vendorBookingCutoff,
    giftType: getGiftableType(experience),
    ticketed: experience.ticketed,
    isExperiencePassport: experience.isExperiencePassport,
    ticketModes: experience.ticketModes,
    hideTimeSlots: experience.hideTimeSlots,
    luxPlus: {},
    ...(experience.features?.transfer && {
      features: {
        transfer: {
          preferredTransfer: experience.features?.transfer?.preferredTransfer,
          postPurchaseCutoffHours: experience.features?.transfer?.postPurchaseCutoffHours,
        },
      },
    }),
    campaignTags: experience.campaigns ?? EmptyArray,
    urgencyTags: experience.urgencyTags ?? EmptyArray,
    disableReviews: experience.disableReviews,
    unlisted: experience.unlisted,
    ...(experience.pricing.margin !== undefined && { mrg: experience.pricing.margin }),
  }

  if (experience.tickets) {
    ;(mapped as any).tickets = experience.tickets.map(
      ({ isLoyaltyEligible }): { isLoyaltyEligible?: boolean } => ({ isLoyaltyEligible }),
    )
  }

  if (experience.reviewsRating) {
    mapped.rating = {
      score: experience.reviewsRating,
      reviewsTotal: experience.reviewsTotal,
      reviewsSource: experience.reviewsSource,
    }
  }

  return mapped
}

type ExperienceDateAvailabilityWithProviderData = ExperiencesContract.DateAvailability & {
  providerData?: App.ExperienceAvailabilityDate['providerData']
}

export function experienceAvailabilityDatesMap(
  date: ExperienceDateAvailabilityWithProviderData,
): App.ExperienceAvailabilityDate {
  return {
    day: date.day,
    soldOut: date.soldOut,
    ...(date.providerData && { providerData: date.providerData }),
  }
}

export function experienceAvailabilityTimesMap(
  groups: Array<ExperiencesContract.TimeGroups>,
  experienceId: string,
): Array<App.ExperienceAvailabilityTimeSlot> {
  return groups.map(group => group.slots.map((slot): App.ExperienceAvailabilityTimeSlot => ({
    time: slot.time,
    groupId: group.featureCode,
    providerData: slot.providerData,
    purchaseLimit: slot.purchaseLimit,
    ...(slot.pickupPoint && {
      pickupPoint: experiencePickupPointMap(slot.pickupPoint),
    }),
    ...(slot.dropoffPoint && {
      dropoffPoint: experiencePickupPointMap(slot.dropoffPoint),
    }),
    tickets: slot.tickets.map((ticket): App.ExperienceItemTicket => ({
      name: group.name ? `${group.name} - ${ticket.fareType}` : ticket.fareType,
      fareType: ticket.fareType,
      groupId: group.featureCode,
      id: ticket.identifier,
      productId: ticket.productId,
      date: ticket.date,
      time: slot.time,
      type: ticket.type,
      // a min buy of 1 actually means *if they are buying it, it has to at least be 1*
      // but we need to also give the option of not buying it, which is therefore 0
      purchaseLimit: {
        min: ticket.purchaseLimit.min === 1 ? 0 : ticket.purchaseLimit.min,
        max: ticket.purchaseLimit.max,
        maxReason: ticket.purchaseLimit.maxReason,
      },
      participantsPerUnit: ticket.participantsPerUnit,
      customerTicketPurchasedAmount: ticket.customerTicketPurchasedAmount,
      price: ticket.salesPrices[0].amount,
      memberPrice: ticket.memberDiscountPrice?.amount ?? 0,
      memberPriceCurrency: ticket.memberDiscountPrice?.currencyCode ?? 'AUD',
      discount: getDiscountPercent(ticket.discount.amount, ticket.salesPrices[0].amount + ticket.discount.amount),
      discounts: {
        base: {
          amount: ticket.discounts.base.amount,
          percentage: ticket.discounts.base.percentage,
        },
        app: {
          amount: ticket.discounts.app.amount,
          percentage: ticket.discounts.app.percentage,
        },
      },
      taxesAndFees: ticket.taxesAndFees?.amount,
      bookByDate: ticket.bookByDate,
      rateStartDate: ticket.rateStartDate,
      rateEndDate: ticket.rateEndDate,
      description: ticket.description,
      imageUrl: ticket.imageUrl,
      providerData: ticket.providerData,
      ...(getTransferTicketExtras(ticket)?.length ? { extras: getTransferTicketExtras(ticket) } : {}),
      isExtra: ticket.isExtra,
      isRequired: ticket.isRequired,
      isLoyaltyEligible: ticket.isLoyaltyEligible,
      bookingOptions: ticket.bookingOptions,
      numberOfAdults: ticket.numberOfAdults,
      numberOfChildren: ticket.numberOfChildren,
      experienceId,
    })),
  }))).flat()
}

export function experienceTransferTimesMap(
  groups: Array<ExperiencesContract.TimeGroups>,
  experienceId: string,
): Array<App.ExperienceTransferOption> {
  return groups.flatMap(group =>
    group.slots.flatMap((slot) =>
      slot.tickets.map((ticket): App.ExperienceTransferOption => {
        const bookingOptions = ticket.bookingOptions ?? {}
        const extras = getTransferTicketExtras(ticket)
        return {
          id: ticket.identifier,
          name: group.name ? `${group.name} - ${ticket.fareType}` : ticket.fareType,
          price: ticket.salesPrices[0].amount,
          memberPrice: ticket.memberDiscountPrice?.amount ?? 0,
          memberPriceCurrency: ticket.memberDiscountPrice?.currencyCode ?? 'AUD',
          // TODO temporarily leaving it blank to check why it was removed from
          // svc-experiences but here wasn't changed
          description: '',
          experienceId,
          purchaseLimit: {
            min: ticket.purchaseLimit.min === 1 ? 0 : ticket.purchaseLimit.min,
            max: ticket.purchaseLimit.max,
            maxReason: ticket.purchaseLimit.maxReason,
          },
          maxPassengers: bookingOptions.maxPassengers ?? 0,
          maxCheckedBags: bookingOptions.maxCheckedBags ?? 0,
          maxCarryOnBags: bookingOptions.maxCarryOnBags ?? 0,
          hasChildSeats: bookingOptions.childSeatTypes?.includes('INFANT-SEAT') ?? false,
          hasBoosterSeats: bookingOptions.childSeatTypes?.includes('BOOSTER-SEAT') ?? false,
          maxTransferDistance: bookingOptions.distanceCovered ?? 100,
          transferTypes: bookingOptions.transferTypes ?? [],
          ...(extras?.length ? { extras } : {}),
          taxesAndFees: ticket.taxesAndFees?.amount,
          bookByDate: ticket.bookByDate,
          rateStartDate: ticket.rateStartDate,
          rateEndDate: ticket.rateEndDate,
          loyaltyOptions: ticket.loyaltyOptions,
          isLoyaltyEligible: ticket.isLoyaltyEligible,
          discounts: {
            base: {
              amount: ticket.discounts.base.amount ?? 0,
              percentage: ticket.discounts.base.percentage ?? 0,
            },
            app: {
              amount: ticket.discounts.app.amount ?? 0,
              percentage: ticket.discounts.app.percentage ?? 0,
            },
          },
          ...(slot.pickupPoint && {
            pickupPoint: experiencePickupPointMap(slot.pickupPoint),
          }),
          ...(slot.dropoffPoint && {
            dropoffPoint: experiencePickupPointMap(slot.dropoffPoint),
          }),
        }
      }),
    ),
  )
}

function getTransferTicketExtras(
  ticket: ExperiencesContract.TimeTicket,
): Array<App.ExperienceTransferExtra> | undefined {
  if (!('extras' in ticket) || !Array.isArray(ticket.extras)) return undefined

  return ticket.extras.map((extra) => ({
    id: extra.id,
    type: extra.type,
    title: extra.title,
    price: extra.price,
    currency: extra.currency,
    purchaseLimit: extra.purchaseLimit,
  }))
}

export function experiencePickupPointMap(
  pickupPoint: ExperiencesContract.PickupPoint,
): App.ExperiencePickupPoint {
  return {
    id: pickupPoint.id,
    name: pickupPoint.description ?? undefined,
    latitude: pickupPoint.latitude ?? undefined,
    longitude: pickupPoint.longitude ?? undefined,
    ticketModeKeys: pickupPoint.ticketModeKeys,
    minutesPrior: pickupPoint.minutesPrior,
    time: pickupPoint.time,
    iataCode: pickupPoint.iataCode,
  }
}

const fieldTypeMap: Record<string, 'text' | 'email' | 'single_choice' | 'date' | 'tel'> = {
  string: 'text',
}

export function experienceCustomerFieldMap(
  field: ExperiencesContract.BookingCustomerInfo,
): App.ExperienceFormField {
  return {
    label: field.label,
    name: field.name,
    required: field.required,
    type: fieldTypeMap[field.type] ?? (field.type as App.ExperienceFormField['type']),
    options: field.options?.map(option => ({
      label: option.label,
      value: option.value,
    })) ?? [],
  }
}

const SUPPORTED_VOUCHER_BARCODE_FORMATS = new Set(['CODE128', 'CODE39'])

function experienceVoucherBarcodesMap(
  barcodes: ExperiencesContract.Voucher['barcodes'],
): App.ExperienceVoucher['barcodes'] {
  if (!barcodes?.length) return undefined

  const mapped = barcodes.flatMap((barcode) => {
    const format = barcode.format.trim().toUpperCase()
    const value = barcode.value.trim()
    if (!value || !SUPPORTED_VOUCHER_BARCODE_FORMATS.has(format)) {
      return []
    }

    return [{
      value,
      format: format as 'CODE128' | 'CODE39',
      ...(barcode.label ? { label: barcode.label } : {}),
    }]
  })

  return mapped.length ? mapped : undefined
}

export function experienceVoucherMap(voucher: ExperiencesContract.Voucher): App.ExperienceVoucher {
  return {
    id: voucher.id,
    expiryDate: voucher.expiryDate,
    voucherCode: voucher.bookingNumber,
    bookingStartDate: voucher.bookingStartDate,
    qrCodeLogoUrl: voucher.qrCodeLogoUrl,
    offer: {
      vendorName: voucher.offer?.vendorName,
      barcodeType: voucher.offer?.barcodeType,
      inclusions: voucher.offer?.inclusionsRendered?.trim() ?? '',
      conditions: voucher.offer?.conditionsRendered?.trim() ?? '',
      howToRedeem: voucher.offer?.howToRedeemRendered?.trim() ?? '',
      whatToRemember: voucher.offer?.whatToRememberRendered?.trim() ?? '',
      voucherCodeRequirements: voucher.offer?.voucherCodeRequirements,
      availability: voucher.offer?.availabilityRendered?.trim() ?? '',
      showOnlyVoucherValue: voucher.offer?.showOnlyVoucherValue,
    },
    customer: voucher.customer ? {
      name: voucher.customer.name,
      email: voucher.customer?.email,
      phone: voucher.customer?.phone,
      surname: voucher.customer?.surname,
    } : undefined,
    location: {
      name: voucher.redemptionLocation?.name,
      city: voucher.redemptionLocation?.city,
      phone: voucher.redemptionLocation?.phone,
      state: voucher.redemptionLocation?.state,
      address: voucher.redemptionLocation?.address,
      postcode: voucher.redemptionLocation?.postcode,
    },
    offerPackage: {
      name: voucher.offerPackage?.name,
      description: voucher.offerPackage?.description,
    },
    qrCodeUrl: voucher.qrCodeUrl,
    policies: voucher.policies || '',
    orderNumber: voucher.orderNumber,
    providerItemId: voucher.providerItemId,
    fareType: voucher.fareType,
    ...(voucher.voucherAmount && { voucherAmount: voucher.voucherAmount }),
    ...(voucher.voucherIndex && { voucherIndex: voucher.voucherIndex }),
    ticketsInfo: voucher.ticketsInfo,
    action: voucher.action,
    details: voucher.details,
    barcodes: experienceVoucherBarcodesMap(voucher.barcodes),
  }
}

export function buildExperiencesPassportFromOffers(
  offer: ExperiencesPassportOfferSource,
  experiences: Array<App.ExperienceOffer>,
  currencyCode: string,
): ExperiencesPassport | undefined {
  const DRIVING_SPEED_KM_PER_HOUR = 40
  const intl = getCurrentIntl()
  const regionCode = new Intl.Locale(intl.locale).region ?? 'AU'
  const property = offer.property
  const locationTitleOverride = property && 'locationTitleOverride' in property ? property.locationTitleOverride : undefined
  const locationHeading = 'locationHeading' in offer ? offer.locationHeading : undefined
  const location = 'location' in offer ? offer.location : undefined
  const destinationName = locationHeading ?? locationTitleOverride ?? location ?? property?.name
  if (!destinationName) return undefined

  const hotelLatitude = property?.latitude
  const hotelLongitude = property?.longitude
  const hotelLocation = typeof hotelLatitude === 'number' && typeof hotelLongitude === 'number' ? {
    latitude: hotelLatitude,
    longitude: hotelLongitude,
  } : undefined
  const hotel = {
    name: property?.name,
    location: hotelLocation,
  }

  const getCategorySymbol = (experience: App.ExperienceOffer): App.OfferFeatureSymbol => {
    const parentCategories = experience.categories.filter(category => category.level === 'parent')
    let selectedCategory = experience.primaryCategory

    if (parentCategories.length) {
      const categoryIndex = [...experience.id].reduce((total, character) => total + character.charCodeAt(0), 0) % parentCategories.length
      selectedCategory = parentCategories[categoryIndex]
    }

    return selectedCategory ? getExperienceCategoryIconSymbol(selectedCategory) : 'line_sightseeing'
  }

  const mapExperienceToInclusion = (experience: App.ExperienceOffer): ExperiencesPassportInclusion => {
    const redemptionLocation = experience.redemptionLocations[0]
    const experienceLocation = redemptionLocation ?? experience.location
    const distanceKm = hotel.location && experienceLocation ? calculateDistance(
      hotel.location.latitude,
      hotel.location.longitude,
      experienceLocation.latitude,
      experienceLocation.longitude,
    ) : undefined
    const drivingTimeMinutes = distanceKm !== undefined ? Math.max(Math.round(distanceKm / DRIVING_SPEED_KM_PER_HOUR * 60), 1) : undefined
    const drivingTimeText = drivingTimeMinutes ? formatMinutesToHoursMinutes(drivingTimeMinutes, intl) : undefined
    const locationName = experienceLocation?.name
    const distanceText = distanceKm !== undefined && hotel.name ? getSearchDistanceLabel(intl, hotel.name, distanceKm * 1000, regionCode) : undefined
    const value = experience.pricing.value || experience.grossPrice || experience.price || undefined

    return {
      id: experience.id,
      title: experience.name,
      description: experience.copy.description || experience.copy.dealDescription || experience.copy.highlights[0],
      value,
      valueCurrencyCode: currencyCode,
      capacityText: experience.ticketUnitLabel,
      howToRedeem: experience.copy.howToRedeem,
      offerConditions: experience.copy.offerConditions,
      image: experience.images[0],
      imageUrl: experience.images[0]?.url,
      images: experience.images,
      rating: experience.rating?.score,
      reviewsText: experience.rating?.reviewsTotal ? formatDecimal(experience.rating.reviewsTotal, {
        regionCode: 'US',
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: experience.rating.reviewsTotal > 1000 ? 0 : 1,
      }).toLowerCase() : undefined,
      locationText: locationName,
      locationAddressText: redemptionLocation ? formatLocationLine(
        redemptionLocation.city,
        redemptionLocation.state,
        redemptionLocation.postalCode,
      ) || undefined : undefined,
      locationDistanceText: distanceText && drivingTimeText ? intl.formatMessage(
        {
          id: 'AtKSNdb5WkoGKMpHUsEi',
          defaultMessage: '{distance} · {drivingTime} drive',
          description: 'Distance and estimated driving time from the accommodation to an Experiences Passport inclusion',
        },
        { distance: distanceText, drivingTime: drivingTimeText },
      ) : distanceText,
      mapRouteText: hotel.name && locationName ? intl.formatMessage(
        {
          id: 'r-xF-fTHJrw9Wx5_KZXs',
          defaultMessage: '{origin} → {destination}',
          description: 'Route direction text showing origin hotel to destination experience on the map',
        },
        { origin: hotel.name, destination: locationName },
      ) : undefined,
      mapOriginText: hotel.name,
      mapDestinationText: locationName,
      mapDrivingTimeText: drivingTimeText,
      mapOriginLocation: hotel.location,
      mapDestinationLocation: experienceLocation ? {
        latitude: experienceLocation.latitude,
        longitude: experienceLocation.longitude,
      } : undefined,
      symbol: getCategorySymbol(experience),
    }
  }

  const inclusions = experiences.map(mapExperienceToInclusion)
  if (!inclusions.length) return undefined
  const totalValue = inclusions.reduce((total, inclusion) => total + (inclusion.value ?? 0), 0)

  return {
    destinationName,
    totalValue: totalValue || undefined,
    totalValueCurrencyCode: currencyCode,
    availableCount: inclusions.length,
    totalCount: inclusions.length,
    inclusions,
  }
}

export function buildExperiencesPassportFromOrderItems(
  offer: ExperiencesPassportOfferSource,
  experienceItems: Array<App.OrderExperienceItem>,
  experiences: Record<string, App.ExperienceOffer>,
  bookingDetails: Record<string, App.ExperienceBookingDetails>,
  vouchers: App.ExperienceState['experienceVoucher'],
  currencyCode: string,
): ExperiencesPassport | undefined {
  const passportExperienceItems = experienceItems.filter(item => experiences[item.experienceId]?.isExperiencePassport)
  const passport = buildExperiencesPassportFromOffers(
    offer,
    passportExperienceItems.flatMap(item => experiences[item.experienceId] ? [experiences[item.experienceId]] : []),
    currencyCode,
  )

  if (!passport) return undefined

  const itemByExperienceId = new Map(passportExperienceItems.map(item => [item.experienceId, item]))

  return {
    ...passport,
    availableCount: passportExperienceItems.filter(item => !bookingDetails[item.id]?.markedAsRedeemed).length,
    totalCount: passportExperienceItems.length,
    inclusions: passport.inclusions.map(inclusion => {
      const item = itemByExperienceId.get(inclusion.id)
      const bookingDetail = item ? bookingDetails[item.id] : undefined
      const voucher = item ? vouchers[item.id]?.data : undefined
      const isExpired = !!item?.expirationDate && new Date(item.expirationDate).getTime() < Date.now()
      const voucherStatus = isExpired ? 'expired' : (bookingDetail?.markedAsRedeemed ? 'used' : 'active')

      return {
        ...inclusion,
        experienceItemId: item?.id,
        capacityText: item?.ticket.name ?? inclusion.capacityText,
        redemptionStatus: voucherStatus === 'used' ? 'used' : 'redeemable',
        voucherStatus,
        voucherCode: bookingDetail?.providerBookingNumber ?? voucher?.voucherCode ?? item?.bookingNumber,
        voucherExpiredAt: isExpired ? item.expirationDate : undefined,
      }
    }),
  }
}
